import type { EventPhase } from './rules'

/**
 * The pre-show (docs/features/pre-show-page.md): before a scheduled event goes live its page shows a countdown on the
 * Ink globe in place of the map and stage, and anyone may register to be emailed when it goes live. Watching never
 * needs registering. These are its rules, apart from React.
 */

/** What the pre-show shows of the registrations: how many, and how many in each time zone. Never who. */
export interface RegistrationSummary {
  count: number
  places: { time_zone: string; count: number }[]
  /** The signed-in viewer's own; false when signed out. */
  registered: boolean
}

/** How long after the start a page with nothing live still opens on the pre-show ("Starting any minute"). Later, a
 *  quiet stretch of a running event keeps its watch screen: the map, the course and the chat. */
export const LATE_START_MS = 30 * 60 * 1000

/**
 * Whether this viewer may get the pre-show at all: the event has it on (`enabled`, its organiser's switch), has a
 * start, it is before it or at most `LATE_START_MS` after it, and the viewer doesn't host the stage (a host needs the watch screen to go live). `forced` (the page's
 * `?preshow`) shows it to a host too, so they can see what their audience sees.
 */
export function preShowEligible({
  enabled,
  startsAt,
  phase,
  canHost,
  forced = false,
  now = Date.now(),
}: {
  enabled: boolean
  startsAt: string | null | undefined
  phase: EventPhase
  canHost: boolean
  forced?: boolean
  now?: number
}): boolean {
  const start = startsAt ? Date.parse(startsAt) : NaN
  if (!enabled || Number.isNaN(start) || phase === 'after' || now - start > LATE_START_MS) return false
  return forced || !canHost
}

/** How long after the room connects something live still counts as already live: the page cuts, no wave. */
export const SETTLE_MS = 3000

/**
 * Where the pre-show is, as the page moves through it:
 * - `deciding`: after the start, the room hasn't said yet whether anything is live, so the watch screen shows;
 * - `waiting`: the pre-show, nothing live;
 * - `entering`: something went live while it showed, and its ring wave is opening the page;
 * - `done`: the watch screen, from then on.
 */
export type PreShowStep = 'deciding' | 'waiting' | 'entering' | 'done'

export function firstPreShowStep({ eligible, phase }: { eligible: boolean; phase: EventPhase }): PreShowStep {
  if (!eligible) return 'done'
  return phase === 'before' ? 'waiting' : 'deciding'
}

/**
 * The next step. `live` is anything on screen being live (a stage's faces, a camera's video). `settledFor` is how long
 * the room has been connected (null while it isn't): until it has been connected for `SETTLE_MS`, live means it was
 * live already, so the page cuts straight to the watch screen.
 */
export function nextPreShowStep(
  step: PreShowStep,
  { eligible, live, settledFor }: { eligible: boolean; live: boolean; settledFor: number | null },
): PreShowStep {
  if (step === 'done' || step === 'entering') return step
  if (!eligible) return 'done'
  const settled = settledFor !== null && settledFor >= SETTLE_MS
  if (live) return step === 'waiting' && settled ? 'entering' : 'done'
  if (step === 'deciding' && settled) return 'waiting'
  return step
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/** The clock's parts until `startsAt`: days (left out when none, at most 99), hours, minutes and seconds. */
export function clockParts(msLeft: number): [value: string, unit: 'd' | 'h' | 'm' | 's'][] {
  const total = Math.floor(Math.max(0, msLeft) / 1000)
  const days = Math.floor(total / 86400)
  const parts: [string, 'd' | 'h' | 'm' | 's'][] = []
  if (days > 0) parts.push([pad2(Math.min(days, 99)), 'd'])
  parts.push([pad2(Math.floor(total / 3600) % 24), 'h'], [pad2(Math.floor(total / 60) % 60), 'm'], [pad2(total % 60), 's'])
  return parts
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** The visually hidden timer's words, which change once a minute: "Starts in 5 days 4 hours 12 minutes". */
export function timerText(msLeft: number): string {
  if (msLeft <= 0) return 'Starting any minute'
  const minutes = Math.ceil(msLeft / 60000)
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor(minutes / 60) % 24
  const words = [days > 0 && plural(days, 'day'), (days > 0 || hours > 0) && plural(hours, 'hour'), plural(minutes % 60, 'minute')]
  return `Starts in ${words.filter(Boolean).join(' ')}`
}

/** The number of time zones registrations come from. */
export const zoneCount = (summary: RegistrationSummary | null) => summary?.places.length ?? 0

/** The browser's IANA time zone, which registering sends so the globe can place the viewer; null when it has none. */
export function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

const icsStamp = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
const icsText = (text: string) => text.replace(/[\;,]/g, (c) => `\\${c}`).replace(/\r?\n/g, '\\n')

/** A one-event calendar file for Add to calendar: an hour long when the event has no end. */
export function calendarFile({
  id,
  name,
  startsAt,
  endsAt,
  url,
  now = new Date(),
}: {
  id: string
  name: string
  startsAt: string
  endsAt?: string | null
  url: string
  now?: Date
}): string {
  const start = new Date(startsAt)
  const end = endsAt ? new Date(endsAt) : new Date(start.getTime() + 3600_000)
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//StreamBridge//Pre-show//EN',
    'BEGIN:VEVENT',
    `UID:${id}@streambridge.live`,
    `DTSTAMP:${icsStamp(now)}`,
    `DTSTART:${icsStamp(start)}`,
    `DTEND:${icsStamp(end)}`,
    `SUMMARY:${icsText(name)}`,
    `URL:${url}`,
    `DESCRIPTION:${icsText(`Watch live: ${url}`)}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n')
}
